import { GeneralAPI } from '../apis/GeneralAPI'
import Header from '../components/Header'
import LandingPageWrapper from '../components/HomePage/LandingPageWrapper'
import BookingSection from '../components/PestLanding/BookingSection'
import CampaignStrategy from '../components/PestLanding/CampaignStrategy'
import FAQ from '../components/PestLanding/FAQ'
import HowItWorks from '../components/PestLanding/HowItWorks'
import PestHero from '../components/PestLanding/PestHero'
import SocialProof from '../components/PestLanding/SocialProof'
import WhyOLM from '../components/PestLanding/WhyOLM'

const FINANCIAL_FALLBACK_META = `
<meta name="description" content="Get higher response rates with targeted direct mail. Proven templates, fast turnaround, and expert coaching to help you close more deals." />
<meta property="og:title" content="Direct Mail Marketing That Works" />
<meta property="og:description" content="Get higher response rates with targeted direct mail. Proven templates, fast turnaround, and expert coaching to help you close more deals." />
`

const HERO_BULLETS = [
    'Target households by geography, age, income, home value, investable-asset indicators, business ownership, and life stage',
    'Build familiarity through recurring touches\u2014not a single postcard that prospects forget',
    'Promote seminars, retirement reviews, second opinions, and other clear reasons to start a conversation',
    'Track calls, QR visits, booked meetings, and response by audience and campaign',
]

const CAMPAIGNS = [
    {
        title: 'Reach rollover prospects',
        description:
            'Introduce your firm to households approaching retirement, changing jobs, or consolidating old accounts\u2014before the decision is urgent.',
        iconBg: 'bg-secondary/10',
        iconColor: 'text-secondary',
        icon: 'bolt',
    },
    {
        title: 'Fill educational events',
        description:
            'Invite a focused local audience to retirement workshops, tax-planning events, Social Security sessions, webinars, or private consultations.',
        iconBg: 'bg-[#FAB331]/20',
        iconColor: 'text-secondary',
        icon: 'home',
    },
    {
        title: 'Build a referral system',
        description:
            'Stay visible around the people your clients may refer, so your name feels familiar when an introduction happens.',
        iconBg: 'bg-[#DBEAFE]',
        iconColor: 'text-[#1E40AF]',
        icon: 'map-pin',
        badge: 'Auto-trigger ready',
    },
    {
        title: 'Re-engage old opportunities',
        description:
            'Give dormant leads, past event attendees, and prospects who said \u201cnot yet\u201d a useful reason to restart the conversation.',
        iconBg: 'bg-[#DCFCE7]',
        iconColor: 'text-[#166534]',
        icon: 'refresh',
    },
    {
        title: 'Own a local niche',
        description:
            'Build campaigns for business owners, physicians, executives, retirees, women in transition, or another audience your firm serves especially well.',
        iconBg: 'bg-secondary/10',
        iconColor: 'text-secondary',
        icon: 'wrench',
    },
    {
        title: 'Show up at the right moment',
        description:
            'Plan touches around tax season, Medicare enrollment, retirement milestones, market volatility, job changes, business exits, and year-end planning.',
        iconBg: 'bg-gray-200',
        iconColor: 'text-gray-600',
        icon: 'building-office',
    },
]

const COMPARISONS = [
    {
        bad: {
            title: 'Basic mailing lists',
            desc: 'Broad ZIPs, routes, or databases with little strategy',
        },
        good: {
            title: 'Smarter audience targeting',
            desc: 'Customer, prospect, geography, demographic, property, business, and behavioral filters',
        },
    },
    {
        bad: {
            title: 'Slow campaign launches',
            desc: 'Missed timing when demand, seasonality, or customer behavior shifts',
        },
        good: {
            title: 'Fast production options',
            desc: 'In-house print and mail with 2-business-day production available',
        },
    },
    {
        bad: {
            title: 'Generic creative',
            desc: 'The same postcard message regardless of audience, lifecycle stage, or offer',
        },
        good: {
            title: 'Campaigns built for advisor growth',
            desc: 'Messaging shaped around your niche, offer, local market, review process, and prospect journey',
        },
    },
    {
        bad: {
            title: 'No response visibility',
            desc: 'Hard to know which campaign created calls',
        },
        good: {
            title: 'Trackable performance',
            desc: 'Call tracking, QR codes, and campaign-level reporting',
        },
    },
    {
        bad: {
            title: 'One-off postcard orders',
            desc: 'No cadence, testing plan, or next step',
        },
        good: {
            title: 'Repeatable growth program',
            desc: 'Audience, offer, format, cadence, and follow-up aligned to your market',
        },
    },
]

const STEPS = [
    {
        num: 1,
        color: '#ED5C2F',
        title: 'Strategy call',
        desc: '20 minutes. We review your ideal client, niche, geography, current growth channels, offers, upcoming events, and review requirements.',
    },
    {
        num: 2,
        color: '#F28230',
        title: 'Audience + offer',
        desc: 'We define the right household segment, timing, message, offer, geography, cadence, and mail format for your growth goal.',
    },
    {
        num: 3,
        color: '#FAB331',
        title: 'Produce + mail',
        desc: 'Our in-house print and mail operation turns approved creative into consistent drops, with versions for different audiences, markets, or advisor teams.',
    },
    {
        num: 4,
        color: '#3F454F',
        title: 'Track + improve',
        desc: 'Use dedicated phone numbers, QR codes, landing pages, booked-meeting data, and campaign response to learn which audiences and messages create conversations.',
    },
]

const FAQS = [
    {
        question:
            'Why use direct mail if most of our growth comes from referrals?',
        answer: 'Direct mail doesn\u2019t replace referrals. It creates familiarity around them and gives your firm a controllable way to reach more households like your best clients\u2014even when introductions are inconsistent.',
    },
    {
        question: 'Can you target the kinds of households we want to serve?',
        answer: 'Yes. Campaigns can use approved consumer, household, property, business, geographic, and life-stage criteria to focus outreach. We\u2019ll help translate your ideal-client profile into a practical audience plan.',
    },
    {
        question:
            'Will this feel like generic \u201cjunk mail\u201d from an advisor?',
        answer: 'It shouldn\u2019t. The strategy, offer, copy, format, and cadence are built around a real prospect concern and a credible next step. Your team approves messaging, disclosures, lists, creative, and final production files before mailing.',
    },
    {
        question: 'What can we ask a prospect to do?',
        answer: 'Common next steps include registering for an educational event, requesting a guide, scheduling a retirement or portfolio review, getting a second opinion, or starting a short introductory conversation.',
    },
]

export default function Financial({ data }) {
    return (
        <LandingPageWrapper sectionSpacing={false} paddingFromTop={false}>
            <Header
                meta={data?.yoast_head || FINANCIAL_FALLBACK_META}
                metaTitle="Direct Mail Marketing That Works"
            />
            <PestHero
                eyebrow="For Financial Advisors + Wealth Firms"
                heading={[
                    'Build a predictable pipeline',
                    'of ideal-fit clients.',
                ]}
                description={
                    'Reach the right prospects before they need an advisor\u2014and stay memorable until a rollover, retirement, inheritance, or second-opinion moment gives them a reason to respond.'
                }
                cta="Plan your client growth campaign"
                fitText="Built for independent advisors, RIAs, and wealth management firms ready to create a consistent prospecting channel beyond referrals and digital ads."
                bullets={HERO_BULLETS}
                image={{
                    src: '/assets/homepage/financial-hero.png',
                    alt: 'Fanned direct mail campaign examples for financial services client acquisition, retention, and win-back',
                    width: 1124,
                    height: 992,
                }}
            />
            <SocialProof
                eyebrow="Trusted by growth-focused companies across the U.S."
                customerLabel="Trusted clients"
            />
            <CampaignStrategy
                eyebrow="Direct mail for financial advisors"
                title="Create demand before prospects raise their hand."
                description={
                    'Most households don\u2019t switch advisors because they saw one ad. Direct mail gives you a tangible, repeatable way to build trust, introduce your point of view, and be remembered when timing changes.'
                }
                campaigns={CAMPAIGNS}
            />
            <WhyOLM
                title="Your next best client may not be searching for an advisor yet."
                paragraphs={[
                    'Advisor growth depends on trust and timing. Referrals are valuable but unpredictable, while digital leads often compare several firms at once. Direct mail helps you build recognition with a defined audience before the need becomes immediate.',
                    'OLM connects audience strategy, advisor-specific messaging, creative, printing, mailing, and response tracking so your firm can run a disciplined prospecting program\u2014not another disconnected marketing tactic.',
                ]}
                comparisons={COMPARISONS}
            />
            <HowItWorks
                title="Launch an advisor direct mail campaign"
                gradientTitle="built to start qualified conversations."
                description="We start with the clients you want more of, the moment that makes your outreach relevant, and the next step a prospect can comfortably take."
                steps={STEPS}
            />
            <FAQ title="Questions financial advisors ask first." faqs={FAQS} />
            <BookingSection
                titleHighlight="financial strategy call."
                containerId="financial-landing-page"
                classPrefix="financial"
            />
            <style
                dangerouslySetInnerHTML={{
                    __html: `
                @media (max-width: 640px) {
                    .financial-form-grid {
                        grid-template-columns: 1fr !important;
                        padding: 20px !important;
                        gap: 16px !important;
                    }
                    .financial-form-footer {
                        justify-content: center !important;
                        text-align: center;
                    }
                    .financial-form-footer button {
                        width: 100%;
                    }
                }
            `,
                }}
            />
        </LandingPageWrapper>
    )
}

export async function getStaticProps() {
    let data = {}
    try {
        const page = await GeneralAPI.getPageBySlug('/financial')
        data = page ? page[0] || {} : {}
    } catch (e) {
        console.error(e)
    }
    try {
        const { banners } = await GeneralAPI.getACFBanners()
        data.banners = banners
    } catch (e) {
        console.error(e)
    }
    return {
        props: {
            data,
            hideCartButton: true,
            hideLoginButton: true,
            phoneNumber: '978-893-5890',
        },
        revalidate: 10,
    }
}
